import { asset, isNull, reminder_rule, service_record, sql, warranty } from "@tendly/powersync";

/**
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │ SOFT-DELETE — CENTRALIZED LIVE LAYER (single choke point)               │
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * This module is the ONLY place where the invariant “live row = `deleted_at IS
 * NULL`” is expressed for this repo's tables with trash (`asset`,
 * `reminder_rule`, `service_record`, `warranty`). Every live read composes
 * these predicates (the domain builders in tendly/queries and the live layer
 * import them from here). The ONLY reads that see deleted rows are the trash
 * (`getTrashedAssetsQuery`) and a harmless id-scoped lookup, both declared
 * in the guard's whitelist (`guard-manifest.ts`).
 *
 * Why centralize? The scattered invariant was the campaign's #1 bug family
 * (stats/counters/PATCH that ignored `deleted_at`). With a single choke
 * point + anti-regression guard, adding a raw read of these tables
 * outside the whitelist BREAKS the build (see
 * `live/__tests__/live-access-guard.test.ts`).
 *
 * Schema note (tendly): `asset` is the PARENT of the hierarchy. `reminder_rule`,
 * `service_record` and `warranty` carry their own `deleted_at` AND an `asset_id`:
 * a child row is LIVE only if its own `deleted_at IS NULL` AND its parent
 * asset is not in the trash (cohort). `usage_reading` and `attachment` are NOT
 * soft-delete: they are retained when the asset is sent to the trash and
 * cascade by FK when its parent `asset` is purged.
 */

/**
 * “Live asset” predicates: the asset is not in the trash. Applies to
 * EVERY live view/query of `asset`. `asset` is the hierarchy root, so
 * its own `deleted_at IS NULL` is enough.
 */
export function liveAssetFilters() {
  return [isNull(asset.deleted_at)];
}

/**
 * Cohort predicate: the child row hangs from an asset that is NOT in the
 * trash. With soft-delete of the parent asset, children persist intact (to
 * restore the entire cohort), but disappear from live views.
 */
function parentAssetNotTrashed(childAssetId: unknown) {
  return sql`NOT EXISTS (SELECT 1 FROM ${asset} WHERE ${asset.id} = ${childAssetId} AND ${asset.deleted_at} IS NOT NULL)`;
}

/**
 * “Live reminder” predicates: the rule is not deleted AND its parent asset
 * is not in the trash either (cohort).
 */
export function liveReminderRuleFilters() {
  return [isNull(reminder_rule.deleted_at), parentAssetNotTrashed(reminder_rule.asset_id)];
}

/**
 * “Live service” predicates: the record is not deleted AND its parent asset
 * is not in the trash (cohort).
 */
export function liveServiceRecordFilters() {
  return [isNull(service_record.deleted_at), parentAssetNotTrashed(service_record.asset_id)];
}

/**
 * “Live warranty” predicates: the warranty is not deleted AND its parent asset
 * is not in the trash (cohort).
 */
export function liveWarrantyFilters() {
  return [isNull(warranty.deleted_at), parentAssetNotTrashed(warranty.asset_id)];
}
